import _typeof from "@babel/runtime/helpers/typeof";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["placement", "color", "size"],
    _excluded2 = ["bgColor"],
    _excluded3 = ["size", "bgColor", "borderColor"],
    _excluded4 = ["children", "target", "hidden", "zIndex", "placement", "boundary", "positionFixed", "arrowSize", "ArrowComponent", "Container", "role", "popperProps", "portalContainer", "unmountOnExit", "mountOnEnter", "onOpen", "onClose", "attachToModal", "duration"],
    _excluded5 = ["onClickAway", "children"],
    _excluded6 = ["hide"],
    _excluded7 = ["target", "children", "containerProps"],
    _excluded8 = ["target", "children", "containerProps"],
    _excluded9 = ["target", "children", "containerProps"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * Generic popover functionality
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { Manager, Popper, Reference } from 'react-popper';
import { boxShadow } from 'styled-system';
import Box from '../Box';
import { isShallowEqual, noop, useThemeDefaults } from '../utils';
import { isKbDismiss, isKbInteraction } from '../utils/kb';
import { Fade } from '../animations/Fade';
import { OpenState as PopoverOpenState } from '../OpenState';
import ReactDOM from 'react-dom';
import { canUseDOM } from '../utils/environment';
import { useCombinedRefs } from '../hooks/useCombineRefs';
import { ModalContext } from '../Modal';
export var OpenState = PopoverOpenState;
export var DropdownContainer = useThemeDefaults(function (props) {
  return {
    position: 'relative',
    boxShadow: props.theme.popover.boxShadow,
    minWidth: props.theme.popover.minWidth,
    bg: props.theme.popover.bgColor,
    border: "".concat(props.theme.borderWidth, " solid"),
    borderColor: props.theme.popover.borderColor,
    borderRadius: props.theme.radius,
    zIndex: 1
  };
}, styled(Box).withConfig({
  displayName: "Popover__DropdownContainer",
  componentId: "sc-8txgrg-0"
})(boxShadow));
DropdownContainer.propTypes = _objectSpread(_objectSpread({}, Box.propTypes), boxShadow.propTypes);
export var TooltipContainer = useThemeDefaults(function (props) {
  return {
    position: 'relative',
    minWidth: props.theme.tooltip.minWidth,
    bg: props.theme.tooltip.bgColor,
    borderRadius: props.theme.radius,
    zIndex: 2
  };
}, Box);
TooltipContainer.propTypes = _objectSpread({}, Box.propTypes);
export var arrowBorders = function arrowBorders(_ref) {
  var placement = _ref.placement,
      color = _ref.color,
      size = _ref.size,
      css = _objectWithoutProperties(_ref, _excluded);

  var base = {
    width: 0,
    height: 0
  };
  var transparent = "".concat(size / 2, "px solid transparent");
  var filled = "".concat(size / 2, "px solid ").concat(color);
  var up = {
    borderLeft: transparent,
    borderRight: transparent,
    borderBottom: filled
  };
  var down = {
    borderLeft: transparent,
    borderRight: transparent,
    borderTop: filled
  };
  var right = {
    borderLeft: filled,
    borderTop: transparent,
    borderBottom: transparent
  };
  var left = {
    borderRight: filled,
    borderTop: transparent,
    borderBottom: transparent
  };

  switch (placement) {
    case 'top':
      return _objectSpread(_objectSpread(_objectSpread({}, base), down), css);

    case 'right':
      return _objectSpread(_objectSpread(_objectSpread({}, base), left), css);

    case 'left':
      return _objectSpread(_objectSpread(_objectSpread({}, base), right), css);

    default:
      return _objectSpread(_objectSpread(_objectSpread({}, base), up), css);
  }
};
export var placeArrow = function placeArrow(props) {
  var offset = -(props.size / 2);

  switch (props.placement) {
    case 'top':
      return {
        bottom: offset
      };

    case 'left':
      return {
        right: offset
      };

    case 'right':
      return {
        left: offset
      };

    default:
      return {
        top: offset
      };
  }
};
export var plainArrow = function plainArrow(_ref2) {
  var bgColor = _ref2.bgColor,
      props = _objectWithoutProperties(_ref2, _excluded2);

  return _objectSpread(_objectSpread({}, placeArrow(props)), arrowBorders({
    size: props.size,
    placement: props.placement,
    color: props.theme.colors[bgColor]
  }));
};
export var borderedArrow = function borderedArrow(_ref3) {
  var size = _ref3.size,
      bgColor = _ref3.bgColor,
      borderColor = _ref3.borderColor,
      props = _objectWithoutProperties(_ref3, _excluded3);

  return _objectSpread(_objectSpread({}, plainArrow(_objectSpread({
    size: size,
    bgColor: borderColor
  }, props))), {}, {
    '::after': arrowBorders({
      size: size - 2,
      content: '""',
      position: 'absolute',
      placement: props.placement,
      color: props.theme.colors[bgColor],
      transform: props.placement === 'left' ? "translate(-".concat(size / 2, "px, -").concat((size - 2) / 2, "px)") : props.placement === 'top' ? "translate(-".concat((size - 2) / 2, "px, -").concat(size / 2, "px)") : props.placement === 'right' ? "translate(1px, -".concat((size - 2) / 2, "px)") : "translate(-".concat((size - 2) / 2, "px, 1px)")
    })
  });
};
export var baseArrowStyles = {
  backgroundColor: 'transparent',
  position: 'absolute'
};
export var MenuArrow = useThemeDefaults(function (props) {
  return {
    bgColor: props.theme.popover.arrowColor,
    borderColor: props.theme.popover.borderColor
  };
}, styled(Box).withConfig({
  displayName: "Popover__MenuArrow",
  componentId: "sc-8txgrg-1"
})(baseArrowStyles, borderedArrow));
MenuArrow.propTypes = _objectSpread(_objectSpread({}, Box.propTypes), {}, {
  bgColor: PropTypes.string,
  borderColor: PropTypes.string,
  size: PropTypes.number.isRequired
});
export var PlainArrow = useThemeDefaults(function (props) {
  return {
    bgColor: props.theme.tooltip.arrowColor
  };
}, styled(Box).withConfig({
  displayName: "Popover__PlainArrow",
  componentId: "sc-8txgrg-2"
})(baseArrowStyles, plainArrow));
PlainArrow.propTypes = _objectSpread(_objectSpread({}, Box.propTypes), {}, {
  bgColor: PropTypes.string
});
export var DEFAULT_POPPER_PROPS = {
  placement: 'bottom',
  modifiers: {
    offset: 0,
    flip: {
      behavior: 'flip'
    },
    preventOverflow: {
      boundariesElement: 'window'
    }
  }
};

var StyleLift = function StyleLift(_ref4) {
  var children = _ref4.children,
      style = _ref4.style,
      liftStyle = _ref4.liftStyle;
  useLayoutEffect(function () {
    liftStyle(style);
  }, [style, liftStyle]);
  return children;
};

var ScheduleUpdate = function ScheduleUpdate(_ref5) {
  var children = _ref5.children,
      content = _ref5.content,
      _ref5$scheduleUpdate = _ref5.scheduleUpdate,
      scheduleUpdate = _ref5$scheduleUpdate === void 0 ? function () {} : _ref5$scheduleUpdate;
  var mountedRef = useRef(false);
  useLayoutEffect(function () {
    if (mountedRef.current) {
      scheduleUpdate();
    }
  }, [content, scheduleUpdate]);
  useEffect(function () {
    mountedRef.current = true;
  }, []);
  return children;
};

export var Base = /*#__PURE__*/React.forwardRef(function (_ref6, ref) {
  var children = _ref6.children,
      target = _ref6.target,
      hidden = _ref6.hidden,
      _ref6$zIndex = _ref6.zIndex,
      zIndex = _ref6$zIndex === void 0 ? 10 : _ref6$zIndex,
      _ref6$placement = _ref6.placement,
      placement = _ref6$placement === void 0 ? 'bottom' : _ref6$placement,
      _ref6$boundary = _ref6.boundary,
      boundary = _ref6$boundary === void 0 ? 'window' : _ref6$boundary,
      positionFixed = _ref6.positionFixed,
      arrowSize = _ref6.arrowSize,
      ArrowComponent = _ref6.ArrowComponent,
      Container = _ref6.Container,
      role = _ref6.role,
      _ref6$popperProps = _ref6.popperProps,
      popperProps = _ref6$popperProps === void 0 ? {} : _ref6$popperProps,
      portalContainer = _ref6.portalContainer,
      unmountOnExit = _ref6.unmountOnExit,
      mountOnEnter = _ref6.mountOnEnter,
      _ref6$onOpen = _ref6.onOpen,
      onOpen = _ref6$onOpen === void 0 ? noop : _ref6$onOpen,
      _ref6$onClose = _ref6.onClose,
      onClose = _ref6$onClose === void 0 ? noop : _ref6$onClose,
      _ref6$attachToModal = _ref6.attachToModal,
      attachToModal = _ref6$attachToModal === void 0 ? true : _ref6$attachToModal,
      duration = _ref6.duration,
      props = _objectWithoutProperties(_ref6, _excluded4);

  var modalContext = useContext(ModalContext);

  var _portalContainer = portalContainer ? portalContainer : attachToModal ? modalContext ? modalContext.modalRef ? modalContext.modalRef.current : null : null : null;

  var _popperProps = _objectSpread(_objectSpread({}, DEFAULT_POPPER_PROPS), {}, {
    placement: placement
  }, popperProps);

  var preventOverflowSettings = _popperProps.modifiers.preventOverflow;

  if (preventOverflowSettings) {
    preventOverflowSettings.boundariesElement = boundary;
  }

  var prevStylesRef = useRef({});
  var fadeRef = useRef(null);

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      style = _useState2[0],
      setStyle = _useState2[1];

  var liftStyle = useCallback(function (style) {
    if (!isShallowEqual(prevStylesRef.current, style)) {
      prevStylesRef.current = style;
      setStyle(style);
    }
  }, []);
  return /*#__PURE__*/React.createElement(Manager, null, /*#__PURE__*/React.createElement(Reference, null, target), children && canUseDOM() ? /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement(Fade, {
    style: style,
    zIndex: zIndex,
    ref: useCombinedRefs(fadeRef, ref),
    m: "".concat(arrowSize / 2 + 5, "px"),
    visible: !hidden,
    unmountOnExit: unmountOnExit,
    mountOnEnter: mountOnEnter,
    onEnter: onOpen,
    onExit: onClose,
    duration: duration
  }, /*#__PURE__*/React.createElement(Popper, _extends({
    placement: placement,
    positionFixed: positionFixed
  }, _popperProps), function (_ref7) {
    var ref = _ref7.ref,
        style = _ref7.style,
        placement = _ref7.placement,
        arrowProps = _ref7.arrowProps,
        scheduleUpdate = _ref7.scheduleUpdate;
    ref(fadeRef.current);
    return /*#__PURE__*/React.createElement(ScheduleUpdate, {
      scheduleUpdate: scheduleUpdate,
      content: children
    }, /*#__PURE__*/React.createElement(StyleLift, {
      style: style,
      liftStyle: liftStyle
    }, /*#__PURE__*/React.createElement(Container, _extends({
      "data-placement": placement,
      role: role,
      "aria-hidden": !!hidden
    }, props), children, /*#__PURE__*/React.createElement(ArrowComponent, _extends({
      size: arrowSize,
      placement: placement
    }, arrowProps)))));
  })), _portalContainer || document.body) : null);
});
process.env.NODE_ENV !== "production" ? Base.propTypes = {
  target: PropTypes.func.isRequired,
  arrowSize: PropTypes.number.isRequired,
  ArrowComponent: PropTypes.any.isRequired,
  Container: PropTypes.any.isRequired,
  hidden: PropTypes.bool,
  placement: PropTypes.string,
  positionFixed: PropTypes.bool,
  role: PropTypes.string,
  popperProps: PropTypes.object,
  zIndex: PropTypes.number,
  portalContainer: PropTypes.object,
  unmountOnExit: PropTypes.bool,
  mountOnEnter: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func
} : void 0;
export var BaseDropdown = withTheme( /*#__PURE__*/React.forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(Base, _extends({
    ref: ref,
    arrowSize: props.theme.popover.arrowSize,
    ArrowComponent: MenuArrow,
    Container: DropdownContainer
  }, props));
}));
export var BaseTooltip = withTheme( /*#__PURE__*/React.forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(Base, _extends({
    ref: ref,
    arrowSize: props.theme.tooltip.arrowSize,
    ArrowComponent: PlainArrow,
    Container: TooltipContainer
  }, props));
}));
export var OuterClickHandler = function OuterClickHandler(_ref8) {
  var onClickAway = _ref8.onClickAway,
      document = _ref8.document,
      children = _ref8.children;
  var ref = useRef(null);
  var targetRef = useRef(null);
  var doc = useMemo(function () {
    return document ? document : (typeof window === "undefined" ? "undefined" : _typeof(window)) === 'object' ? window.document : function () {};
  }, [document]);
  var onClickAwayCallback = useCallback(function (e) {
    if (ref.current && ref.current.contains(e.target) || targetRef.current && targetRef.current.contains(e.target)) {
      return;
    }

    return onClickAway(e);
  }, [ref, targetRef, onClickAway]);
  useEffect(function () {
    doc.addEventListener('click', onClickAwayCallback, true);
    return function () {
      return doc.removeEventListener('click', onClickAwayCallback, true);
    };
  }, [doc, onClickAwayCallback]);
  return children({
    ref: ref,
    targetRef: targetRef
  });
};
export var ClickAwayOpenState = /*#__PURE__*/React.memo(function (_ref9) {
  var _ref9$onClickAway = _ref9.onClickAway,
      _onClickAway = _ref9$onClickAway === void 0 ? function () {} : _ref9$onClickAway,
      children = _ref9.children,
      props = _objectWithoutProperties(_ref9, _excluded5);

  return /*#__PURE__*/React.createElement(OpenState, props, function (_ref10) {
    var hide = _ref10.hide,
        innerProps = _objectWithoutProperties(_ref10, _excluded6);

    return /*#__PURE__*/React.createElement(OuterClickHandler, {
      onClickAway: function onClickAway() {
        hide();

        _onClickAway();
      }
    }, function (_ref11) {
      var ref = _ref11.ref,
          targetRef = _ref11.targetRef;
      return children(_objectSpread({
        containerRef: ref,
        targetRef: targetRef,
        hide: hide
      }, innerProps));
    });
  });
});
export var Tooltip = function Tooltip(_ref12) {
  var _target = _ref12.target,
      children = _ref12.children,
      containerProps = _ref12.containerProps,
      tooltipProps = _objectWithoutProperties(_ref12, _excluded7);

  return /*#__PURE__*/React.createElement(ClickAwayOpenState, null, function (_ref13) {
    var isHidden = _ref13.isHidden,
        show = _ref13.show,
        hide = _ref13.hide,
        setHidden = _ref13.setHidden,
        containerRef = _ref13.containerRef;
    return /*#__PURE__*/React.createElement(Box, _extends({
      display: "inline"
    }, containerProps), /*#__PURE__*/React.createElement(BaseTooltip, _extends({
      ref: containerRef,
      hidden: isHidden,
      role: "tooltip",
      placement: "right",
      target: function target(_ref14) {
        var ref = _ref14.ref;
        return _target({
          ref: ref,
          show: show,
          hide: hide,
          isHidden: isHidden,
          setHidden: setHidden
        });
      }
    }, tooltipProps), children));
  });
};
export var Dropdown = function Dropdown(_ref15) {
  var _target2 = _ref15.target,
      children = _ref15.children,
      containerProps = _ref15.containerProps,
      dropdownProps = _objectWithoutProperties(_ref15, _excluded8);

  return /*#__PURE__*/React.createElement(ClickAwayOpenState, null, function (_ref16) {
    var isHidden = _ref16.isHidden,
        show = _ref16.show,
        hide = _ref16.hide,
        setHidden = _ref16.setHidden,
        containerRef = _ref16.containerRef;
    return /*#__PURE__*/React.createElement(Box, _extends({
      display: "inline"
    }, containerProps), /*#__PURE__*/React.createElement(BaseDropdown, _extends({
      ref: containerRef,
      hidden: isHidden,
      role: "tooltip",
      placement: "right",
      target: function target(_ref17) {
        var ref = _ref17.ref;
        return _target2({
          ref: ref,
          show: show,
          hide: hide,
          isHidden: isHidden,
          setHidden: setHidden
        });
      }
    }, dropdownProps), children));
  });
};
export var findFocusable = function findFocusable(el) {
  return el ? el.querySelector('[tabindex="1"]') || el.querySelector('button,a,input,select,textarea') : null;
};
export var DropdownMenu = /*#__PURE__*/React.forwardRef(function (_ref18, ref) {
  var _target3 = _ref18.target,
      children = _ref18.children,
      _ref18$containerProps = _ref18.containerProps,
      containerProps = _ref18$containerProps === void 0 ? {} : _ref18$containerProps,
      dropdownProps = _objectWithoutProperties(_ref18, _excluded9);

  var firstFocusableRef = useRef(null);
  var focusFirstInteractable = useCallback(function () {
    setTimeout(function () {
      var el = findFocusable(firstFocusableRef.current);

      if (el) {
        el.focus({
          preventScroll: true
        });
      } else {
        console.warn('DropdownMenu should contain interactive elements');
      }
    }, 0);
  }, [firstFocusableRef]);
  return /*#__PURE__*/React.createElement(ClickAwayOpenState, null, function (_ref19) {
    var isHidden = _ref19.isHidden,
        show = _ref19.show,
        hide = _ref19.hide,
        setHidden = _ref19.setHidden,
        containerRef = _ref19.containerRef,
        targetRef = _ref19.targetRef;
    var renderedChildren = children({
      isHidden: isHidden,
      show: show,
      hide: hide,
      setHidden: setHidden
    });
    return /*#__PURE__*/React.createElement(Box, _extends({
      display: "inline",
      ref: targetRef
    }, containerProps), /*#__PURE__*/React.createElement(BaseDropdown, _extends({
      onOpen: focusFirstInteractable,
      ref: useCombinedRefs(containerRef, ref),
      hidden: isHidden,
      role: "menu",
      placement: "bottom",
      onKeyDown: function onKeyDown(e) {
        if (isKbDismiss(e)) {
          hide();
        }
      },
      target: function target(_ref20) {
        var ref = _ref20.ref;
        return _target3({
          ref: ref,
          show: show,
          hide: hide,
          isHidden: isHidden,
          setHidden: setHidden,
          targetProps: {
            ref: ref,
            onClick: function onClick() {
              return setHidden(!isHidden);
            }
          }
        });
      }
    }, dropdownProps), renderedChildren ? /*#__PURE__*/React.cloneElement(React.Children.only(renderedChildren), {
      ref: firstFocusableRef
    }) : null));
  });
});
export { isKbInteraction, isKbDismiss };